import React, { useEffect, useState } from "react";
import { CampaignProductsProps } from "./types";
import { Grid, Switch } from "@material-ui/core";
import useStyles from "./styles";
import {
  getCurrencyPositiveOrNegativeColoring,
  getTrendIndication
} from "../../shared/tableFunctions";
import BarChart from "../../components/BarChart";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { CampaignProductsStatisticsState } from "../../../../store/statistics/campaignproducts/types";
import { thunkFetchCampaignProductsStatistics } from "../../../../store/statistics/campaignproducts/actions";
import DateRangePicker from "../../../../components/inputs/daterangepicker";
import moment from "moment";
import { CampaignTargetUserType } from "../../../../models/Campaign";
import StatTable from "../../components/StatTable";
import { Currency } from "../../../../components/misc/Currency";
import { useI18n } from "../../../../hooks/useI18n";

const CampaignProducts = ({
  campaignId,
  targetUserType,
  periodFrom,
  periodTo
}: CampaignProductsProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const disableReference = targetUserType === CampaignTargetUserType.Segments;
  const { t } = useI18n();

  const [dateRange, setDateRange] = useState([
    moment(periodFrom)
      .subtract(moment(periodTo).diff(moment(periodFrom), "days") + 1, "days")
      .toDate(),
    moment(periodFrom).subtract(1, "days").toDate()
  ]);
  const [showGraph, setShowGraph] = useState(false);

  const campaignProductsStatisticsState = useSelector<
    AppState,
    CampaignProductsStatisticsState
  >(s => s.campaignProductsStatistics);

  let {
    campaignProductsStatistics,
    campaignComparisonProductStatistics,
    isLoading,
    isLoadingCompare
  } = campaignProductsStatisticsState;

  useEffect(() => {
    if (campaignId && periodFrom && periodTo) {
      dispatch(
        thunkFetchCampaignProductsStatistics(
          periodFrom,
          periodTo,
          campaignId,
          targetUserType,
          false
        )
      );
    }
  }, [campaignId, periodFrom, periodTo]);

  useEffect(() => {
    if (campaignId && dateRange && !disableReference) {
      dispatch(
        thunkFetchCampaignProductsStatistics(
          dateRange[0],
          dateRange[1],
          campaignId,
          targetUserType,
          true
        )
      );
    }
  }, [campaignId, dateRange, disableReference]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  return (
    <div>
      <Grid
        className={
          showGraph ? classes.switchContainer : classes.switchContainerAbsolute
        }
        container
        justifyContent="flex-start"
        alignItems="center"
        spacing={0}
      >
        <Grid item>{t("common.messages.table")}</Grid>
        <Grid item component="label" className={classes.switch}>
          <Switch
            size={"small"}
            checked={showGraph}
            onChange={handleChange}
            name="graphSwitch"
            disabled={disableReference || isLoading || isLoadingCompare}
          />
        </Grid>
        <Grid item>{t("common.messages.chart")}</Grid>
      </Grid>

      {showGraph ? (
        <BarChart
          chartData={{
            labels: campaignProductsStatistics.map(p => p.productName),
            datasets: [
              {
                label: t("common.tableHeaders.sales"),
                rgbColor: { r: 5, g: 89, b: 141 },
                data: campaignProductsStatistics.map(p => p.sales),
                stack: "stack 0"
              },
              {
                label: t("common.tableHeaders.salesCampaign"),
                rgbColor: { r: 31, g: 132, b: 52 },
                data: campaignProductsStatistics.map(p => p.salesCampaign),
                stack: "stack 0"
              },
              {
                label: t("common.tableHeaders.salesReference"),
                rgbColor: { r: 92, g: 89, b: 86 },
                data: campaignProductsStatistics.map(
                  p =>
                    campaignComparisonProductStatistics.find(
                      c => c.productId === p.productId
                    )?.sales ?? 0
                ),
                stack: "stack 1"
              }
            ]
          }}
          options={{
            scales: {
              x: {
                stacked: true
              },
              y: {
                stacked: true
              }
            }
          }}
          showLegend={true}
          isLoading={isLoading || (isLoadingCompare && !disableReference)}
        />
      ) : (
        <>
          <StatTable
            exportName={"Campaign product statistics"}
            isLoading={isLoading || (isLoadingCompare && !disableReference)}
            columns={[
              {
                title: "Product",
                field: "name",
                cellStyle: { padding: "8px" }
              },
              {
                title: t("common.tableHeaders.sales"),
                field: "sales",
                cellStyle: { padding: "8px" },
                render: (rowData: any) =>
                  disableReference
                    ? getCurrencyPositiveOrNegativeColoring(rowData.sales)
                    : getTrendIndication(
                        rowData.salesCampaign + rowData.sales,
                        rowData.salesCompare,
                        true
                      )
              },
              {
                title: "of which is campaign",
                field: "salescamp",
                cellStyle: { padding: "8px" },
                hidden: disableReference,
                render: (rowData: any) => (
                  <Currency value={rowData.salesCampaign} />
                )
              },
              {
                title: "Profit / Cost",
                field: "profit",
                cellStyle: {
                  padding: "8px",
                  borderLeft: "1px solid #e9e5e2",
                  backgroundColor: "rgba(5,89,141, 0.1)"
                },
                render: (rowData: any) =>
                  disableReference
                    ? getCurrencyPositiveOrNegativeColoring(rowData.profit)
                    : getTrendIndication(
                        rowData.profitCampaign + rowData.profit,
                        rowData.profitCompare,
                        true
                      )
              },
              {
                title: "of which is campaign",
                field: "profitcamp",
                cellStyle: { padding: "8px" },
                hidden: disableReference,
                render: (rowData: any) => (
                  <Currency value={rowData.profitCampaign} />
                )
              },
              {
                title: t("common.tableHeaders.quantitySold"),
                field: "quantity",
                cellStyle: { padding: "8px", borderLeft: "1px solid #e9e5e2" },
                render: (rowData: any) =>
                  disableReference
                    ? rowData.quantity
                    : getTrendIndication(
                        rowData.quantityCampaign + rowData.quantity,
                        rowData.quantityCompare,
                        false
                      )
              },
              {
                title: "of which is campaign",
                field: "quantityCampaign",
                cellStyle: { padding: "8px" },
                hidden: disableReference
              }
            ]}
            data={
              !isLoading && (!isLoadingCompare || disableReference)
                ? campaignProductsStatistics.map(p => ({
                    productId: p.productId,
                    name: p.productName,
                    sales: p.sales,
                    salesCampaign: p.salesCampaign,
                    salesCompare:
                      campaignComparisonProductStatistics.find(
                        c => c.productId === p.productId
                      )?.sales ?? 0,
                    profit: p.profit,
                    profitCampaign: p.profitCampaign,
                    profitCompare:
                      campaignComparisonProductStatistics.find(
                        c => c.productId === p.productId
                      )?.profit ?? 0,
                    quantity: p.quantitySold,
                    quantityCampaign: p.quantitySoldCampaign,
                    quantityCompare:
                      campaignComparisonProductStatistics.find(
                        c => c.productId === p.productId
                      )?.quantitySold ?? 0
                  }))
                : []
            }
            pageSize={5}
            pageSizeOptions={[5, 10, 20]}
            dense
          />
          {!disableReference ? (
            <>
              <Grid item className={classes.dateRangeContainer}>
                <span>{t("common.formFields.referencePeriod")}:</span>
                <DateRangePicker
                  disabled={isLoading || isLoadingCompare}
                  onChange={setDateRange}
                  value={dateRange}
                />
              </Grid>
              <StatTable
                exportName={"Campaign product reference statistics"}
                isLoading={isLoading || isLoadingCompare}
                columns={[
                  {
                    title: "Product",
                    field: "name",
                    cellStyle: { padding: "8px" }
                  },
                  {
                    title: t("common.tableHeaders.salesReference"),
                    field: "salesCompare",
                    cellStyle: { padding: "8px" },
                    render: (rowData: any) => (
                      <Currency value={rowData.salesCompare} />
                    )
                  },
                  {
                    title: "Profit / Cost reference",
                    field: "profitCompare",
                    cellStyle: {
                      padding: "8px",
                      borderRight: "1px solid #e9e5e2",
                      backgroundColor: "rgba(5,89,141, 0.1)"
                    },
                    render: (rowData: any) => (
                      <Currency value={rowData.profitCompare} />
                    )
                  },
                  {
                    title: "Quantity sold reference",
                    field: "quantityCompare",
                    cellStyle: { padding: "8px" }
                  }
                ]}
                data={
                  !isLoading && !isLoadingCompare
                    ? campaignProductsStatistics.map(p => ({
                        productId: p.productId,
                        name: p.productName,
                        salesCompare:
                          campaignComparisonProductStatistics.find(
                            c => c.productId === p.productId
                          )?.sales ?? 0,
                        profitCompare:
                          campaignComparisonProductStatistics.find(
                            c => c.productId === p.productId
                          )?.profit ?? 0,
                        quantityCompare:
                          campaignComparisonProductStatistics.find(
                            c => c.productId === p.productId
                          )?.quantitySold ?? 0
                      }))
                    : []
                }
                pageSize={5}
                pageSizeOptions={[5, 10, 20]}
                dense
              />
            </>
          ) : (
            <></>
          )}
        </>
      )}
    </div>
  );
};
export default CampaignProducts;
